import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Spinner, Stack } from "@altimateai/lego";
import SubscriptionTypeStep from "./components/wizard/steps/subscriptionType/SubscriptionTypeStep";
import SubscriptionPreview from "./components/wizard/SubscriptionPreview";
import SendTestButton from "./components/wizard/SendTestButton";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { SubscriptionV2Template } from "@/pages/subscriptions/services/types";
import { SubscriptionFormData, SubscriptionFormSchema } from "@/pages/subscriptions/services/types";
import CustomizeSubscriptionStep from "@/pages/subscriptions/components/wizard/steps/customizeSubscription/CustomizeSubscriptionStep";
import DestinationStep from "@/pages/subscriptions/components/wizard/steps/destinationConfig/DestinationStep";
import {
  SubscriptionFormFooter,
  SubscriptionFormHeader,
  useSubscriptionFormData,
  useSubscriptionFormHandlers,
} from "@/pages/subscriptions/components/wizard";
import type { WizardStep } from "./components/wizard/types";
import { ChannelType, SubscriptionType, WizardStepEnum } from "@/pages/subscriptions/constants";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { AlertsTracking } from "@/lib/tracking/alerts";
import { buildPreviewParams } from "./components/wizard/utils/buildPreviewParams";

const SubscriptionNewPage = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const templateData = location.state?.template as SubscriptionV2Template | undefined;

  const isEditMode = Boolean(id);
  const isFromTemplate = Boolean(templateData);

  const [currentStep, setCurrentStep] = useState<WizardStep>(
    isFromTemplate || isEditMode ? WizardStepEnum.CUSTOMIZE : WizardStepEnum.SUBSCRIPTION_TYPE
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    watch,
    setValue,
    setError,
    reset,
  } = useForm<SubscriptionFormData>({
    resolver: zodResolver(SubscriptionFormSchema),
    defaultValues: {
      name: "",
      rule_type: SubscriptionType.REPORT,
      platform: "",
      source: "",
      metric: "",
      filters: [],
      prompt: "",
      variables: [],
      channels: [],
      email_recipients: [],
      slack_channels: [],
    },
    mode: "onBlur",
  });

  // Load subscription/template data and manage form state
  const {
    subscriptionLoading,
    metricsLoading,
    metricsData,
    categoryDisplayNameLookup,
    metricDisplayNameLookup,
    categoryToSourceLookup,
    groupedBySource,
    selectedMetricDefinition,
  } = useSubscriptionFormData({
    subscriptionId: id,
    templateData,
    isEditMode,
    setValue,
    reset,
    watch,
    setCurrentStep,
  });

  useEffect(() => {
    AlertsTracking.createFormLoaded();
  }, []);

  // Form handlers and mutations
  const {
    subscriptionMutation,
    handleClose,
    handleSubscriptionTypeNext,
    handleCustomizeNext,
    handleDestinationSubmit,
    handleGoToStep,
  } = useSubscriptionFormHandlers({
    subscriptionId: id,
    isEditMode,
    metricsData,
    trigger,
    watch,
    setValue,
    setError,
    handleSubmit,
    setCurrentStep,
  });

  // Watch destination fields for validation
  const channels = watch("channels");
  const emailRecipients = watch("email_recipients");
  const slackChannels = watch("slack_channels");

  // Watch fields for preview - debounced via useMemo
  const subscriptionType = watch("rule_type");
  const subscriptionName = watch("name");
  const metric = watch("metric");
  const source = watch("source");
  const filters = watch("filters");
  const variables = watch("variables");

  useBreadcrumb(isEditMode ? "Edit" : "New", {
    defaultParents: [
      { label: "Settings", pathname: "/settings", noLink: true },
      { label: "Subscriptions", pathname: "/subscriptions" },
      ...(isEditMode && subscriptionName
        ? [{ label: subscriptionName, pathname: `/subscriptions/${id}` }]
        : []),
    ],
  });

  // Build preview params from form state
  const previewParams = useMemo(
    () =>
      buildPreviewParams(subscriptionType, subscriptionName, metric, source, filters, variables),
    [subscriptionType, subscriptionName, metric, source, filters, variables]
  );

  // Validate destination step
  const isDestinationValid = useMemo(() => {
    if (!channels || channels.length === 0) return false;

    if (channels.includes(ChannelType.EMAIL)) {
      if (!emailRecipients || emailRecipients.length === 0) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const allEmailsValid = emailRecipients.every(email => emailRegex.test(email.trim()));
      if (!allEmailsValid) return false;
    }

    if (channels.includes(ChannelType.SLACK)) {
      if (!slackChannels || slackChannels.length === 0) return false;
    }

    return true;
  }, [channels, emailRecipients, slackChannels]);

  const isLoading = subscriptionLoading || metricsLoading;
  const isSubmitDisabled =
    currentStep !== WizardStepEnum.DESTINATION ||
    !isDestinationValid ||
    subscriptionMutation.isPending;

  return (
    <Stack direction="column" className="al-h-screen">
      <SubscriptionFormHeader isEditMode={isEditMode} isFromTemplate={isFromTemplate} />

      {isLoading && (
        <Stack direction="row" align="center" justify="center" gap="sm" className="al-h-full">
          <Spinner />
          <span className="al-text-gray-500">Loading subscription controls...</span>
        </Stack>
      )}

      {!isLoading && (
        <Stack
          direction="row"
          className="al-flex-1 al-overflow-hidden al-h-screen al-max-h-screen al-min-h-screen"
        >
          <Stack direction="column" gap="none" className="al-flex-1 al-overflow-hidden">
            <form className="al-flex-1 al-rounded-lg al-bg-background al-overflow-hidden">
              <Stack direction="column" className="al-h-full">
                <div className="al-flex-1 al-overflow-y-auto al-rounded-t-lg al-bg-background">
                  {currentStep === WizardStepEnum.SUBSCRIPTION_TYPE && (
                    <SubscriptionTypeStep
                      control={control}
                      errors={errors}
                      setValue={setValue}
                      onNext={handleSubscriptionTypeNext}
                      metricsData={metricsData}
                      groupedBySource={groupedBySource}
                      categoryDisplayNameLookup={categoryDisplayNameLookup}
                      categoryToSourceLookup={categoryToSourceLookup}
                      isEditMode={isEditMode}
                      isFromTemplate={isFromTemplate}
                    />
                  )}

                  {currentStep === WizardStepEnum.CUSTOMIZE && (
                    <CustomizeSubscriptionStep
                      control={control}
                      onNext={handleCustomizeNext}
                      onGoToStep={handleGoToStep}
                      categoryDisplayNameLookup={categoryDisplayNameLookup}
                      metricDisplayNameLookup={metricDisplayNameLookup}
                      selectedMetricDefinition={selectedMetricDefinition}
                    />
                  )}

                  {currentStep === WizardStepEnum.DESTINATION && (
                    <DestinationStep
                      control={control}
                      errors={errors}
                      onGoToStep={handleGoToStep}
                    />
                  )}
                </div>

                <SubscriptionFormFooter
                  isEditMode={isEditMode}
                  isSubmitDisabled={isSubmitDisabled}
                  isLoading={subscriptionMutation.isPending}
                  isError={subscriptionMutation.isError}
                  onCancel={handleClose}
                  onSubmit={handleDestinationSubmit}
                />
              </Stack>
            </form>
          </Stack>

          <div className="al-flex-1">
            <SubscriptionPreview
              previewParams={previewParams}
              enabled={
                currentStep === WizardStepEnum.CUSTOMIZE ||
                currentStep === WizardStepEnum.DESTINATION
              }
              metricUnit={selectedMetricDefinition?.unit}
              actions={<SendTestButton control={control} />}
            />
          </div>
        </Stack>
      )}
    </Stack>
  );
};

export default SubscriptionNewPage;
